import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { EventsLogFieldGroupTypeFull } from 'src/app/modules/stations/events-list/model/EventsLogFieldGroupTypeOverwrite';
import { AlarmStateFrench, AlarmStateTranslations } from 'src/app/shared/models/alarm/alarm-filter';
import { IconType } from 'src/app/tinea-components/icon/enum/icon-type';
import { TableModel } from 'src/app/tinea-components/table/models/input/table-model';
import { TableRows } from 'src/app/tinea-components/table/models/input/table-rows';
import { TableTd } from 'src/app/tinea-components/table/models/input/table-td';
import { TableTh } from 'src/app/tinea-components/table/models/input/table-th';
import { ValueCell } from 'src/app/tinea-components/table/models/input/value-cell';
import { EventLogEntry, EventLogEntryList, EventsLogFieldGroupType, EventType } from 'src/swagger-gen/web-secured/models';

@Injectable({
  providedIn: 'root',
})
export class TableEventListMapper {
  // -- configuration
  readonly CSS_CLASS_CONFIG = 'table-events';

  // dataId
  readonly COLUMN_ID_DATE = EventsLogFieldGroupTypeFull.DATE;
  readonly COLUMN_ID_LAST_DATE = EventsLogFieldGroupTypeFull.LAST_DATE;
  readonly COLUMN_COUNT_ACQUISITION = EventsLogFieldGroupTypeFull.COUNT_ACQUISITION;
  readonly COLUMN_ID_TAG = EventsLogFieldGroupTypeFull.TAG;
  readonly COLUMN_ID_DETAIL = EventsLogFieldGroupTypeFull.DESCRIPTION;
  readonly COLUMN_ID_VALUE = EventsLogFieldGroupTypeFull.VALUE;
  readonly COLUMN_ID_ACQUISITION = "ACQUISITION";
  readonly CSS_CLASS_SERIOUSNESS_ACQUIT = 'acquit';

  /**
   * Current application language
   */
  language: 'fr' | 'en' = 'fr';

  table: TableModel;

  alarmStateTranslations = AlarmStateTranslations;

  constructor(private translate: TranslateService) {
    this.table = new TableModel();
  }

  init(eventsList: EventLogEntryList, filter: any): TableModel {
    this.table = new TableModel();
    // configuration
    this.initConfiguration();
    // header
    this.initHeader();
    // data
    this.initDataRow(eventsList, filter);

    return this.table;
  }

  initConfiguration(): void {
    this.table.configuration.cssClass = this.CSS_CLASS_CONFIG;
    this.table.configuration.noDataRowMsg = this.translate.instant('error.no_result');
  }

  initHeader(): void {
    const thDate = new TableTh();
    thDate.columnId = this.COLUMN_ID_DATE;
    const thDateCell = new ValueCell();
    thDateCell.displayedValue = this.translate.instant(
      'eventslog.date'
    );
    thDate.valueCells.push(thDateCell);

    const thLastDate = new TableTh();
    thLastDate.show = false;
    thLastDate.columnId = this.COLUMN_ID_LAST_DATE;
    const thLastDateCell = new ValueCell();
    thLastDateCell.displayedValue = this.translate.instant(
      'eventslog.lastDateAcquisitionColumn'
    );
    thLastDate.valueCells.push(thLastDateCell);

    const thNbAcquisitions = new TableTh();
    thNbAcquisitions.show = false;
    thNbAcquisitions.columnId = this.COLUMN_COUNT_ACQUISITION;
    const thNbAcquisitionsCell = new ValueCell();
    thNbAcquisitionsCell.displayedValue = this.translate.instant(
      'eventslog.countNbAcquisitionColumn'
    );
    thNbAcquisitions.valueCells.push(thNbAcquisitionsCell);

    const thTag = new TableTh();
    thTag.columnId = this.COLUMN_ID_TAG;
    const thTagCell = new ValueCell();
    thTagCell.displayedValue = this.translate.instant(
      'eventslog.tag'
    );
    thTag.valueCells.push(thTagCell);

    const thDetail = new TableTh();
    thDetail.columnId = this.COLUMN_ID_DETAIL;
    const thDetailCell = new ValueCell();
    thDetailCell.displayedValue = this.translate.instant(
      'eventslog.details'
    );
    thDetail.valueCells.push(thDetailCell);

    const thValue = new TableTh();
    thValue.columnId = this.COLUMN_ID_VALUE;
    const thValueCell = new ValueCell();
    thValueCell.displayedValue = this.translate.instant(
      'eventslog.value'
    );
    thValue.valueCells.push(thValueCell);

    const thAcquisition = new TableTh();
    thAcquisition.columnId = this.COLUMN_ID_ACQUISITION;
    const thAcquisitionCell = new ValueCell();
    thAcquisitionCell.displayedValue = this.translate.instant(
      'eventslog.acquisition'
    );
    thAcquisition.valueCells.push(thAcquisitionCell);

    const thList = [thDate, thLastDate, thNbAcquisitions, thTag, thDetail, thValue, thAcquisition];
    this.setHeaderCells(thList);
  }

  initDataRow(eventsList: EventLogEntryList, filter: any): void {
    eventsList.list.forEach((event) => {
      const tableRows = new TableRows(event);
      // red color for alarms
      // if (filter.filters.find(x => x.field === EventLogSearchFilter.IS_ALARM)) {
      //   const translate: string = this.translate.instant('alarms.states.raised');
      //   tableRows.row.cssClass = (tableRows.row.obj.value === translate.toLocaleLowerCase()) ? this.CSS_CLASS_SERIOUSNESS_ACQUIT : null;
      // }
      // td - date
      tableRows.row.cssClass = (event.isAlarm) ? this.CSS_CLASS_SERIOUSNESS_ACQUIT : null;
      const date = new TableTd();
      const dateCell = new ValueCell();
      dateCell.displayedValue = event.plcDateTime || '';
      dateCell.dataType = event.plcDateTime ? 'DATE' : 'STRING';
      date.valueCells.push(dateCell);

      // td - date
      const lastDate = new TableTd();
      lastDate.show = false;
      const lastDateCell = new ValueCell();
      lastDateCell.displayedValue = event.sameValueLastAcquisitionDateTime || '';
      lastDateCell.dataType = event.sameValueLastAcquisitionDateTime ? 'DATE' : 'STRING';
      lastDate.valueCells.push(lastDateCell);

      // td - nbAcquisitions
      const nbAcquisitions = new TableTd();
      nbAcquisitions.show = false;
      const nbAcquisitionsCell = new ValueCell();
      nbAcquisitionsCell.displayedValue = event.sameValueAcquisitionCount || '';
      nbAcquisitions.valueCells.push(nbAcquisitionsCell);

      // td - tag
      const tag = new TableTd();
      const tagCell = new ValueCell();
      tagCell.displayedValue = event.displayName;
      tag.valueCells.push(tagCell);

      // td - detail
      const detail = new TableTd();
      const detailCell = new ValueCell();
      detailCell.displayedValue = event.message;
      detail.valueCells.push(detailCell);

      // td - value
      const value = new TableTd();
      const valueCell = new ValueCell();

      if (event.isAlarm === true) {
        valueCell.displayedValue = this.getEventAlarmState(event.value);
      } else {
        valueCell.displayedValue = event.value;
      }

      value.valueCells.push(valueCell);

      const acquisition = new TableTd();
      const acquisitionCell = new ValueCell();

      if(event.sameValueLastAcquisitionDateTime && event.sameValueAcquisitionCount) {
        acquisitionCell.addIcon(
          'BEFORE',
          IconType.FONTAWESOME,
          'fa fa-circle acquisition-warning-icon',
          ''
        );
        acquisitionCell.tooltip = `${this.translate.instant('eventslog.countNbAcquisition')}: ${(event.sameValueAcquisitionCount ? event.sameValueAcquisitionCount : "")} \n
        ${this.translate.instant('eventslog.lastDateAcquisition')}: ${(event.sameValueLastAcquisitionDateTime ? moment(event.sameValueLastAcquisitionDateTime).format("DD/MM/YYYY HH:mm:ss") : "")}`;
     } else {
        acquisitionCell.addIcon(
          'BEFORE',
          IconType.FONTAWESOME,
          'fa fa-circle acquisition-icon',
          ''
        );
      }
      acquisitionCell.displayedValue = "";
      acquisition.valueCells.push(acquisitionCell);

      // add cells
      // ================>
      tableRows.cells = [date, lastDate, nbAcquisitions, tag, detail, value, acquisition];

      // push row
      // ================>
      this.table.dataRow.push(tableRows);
    });
  }

  setHeaderCells(thList: TableTh[]): void {
    this.table.header.cells = thList;
  }

  getValue(event: EventLogEntry): string {
    if (event.eventType === EventType.ALARM_ACKNOWLEDGEMENT) {
      return event.acknowledgedValue === true
        ? AlarmStateFrench.RAISED_ACKNOWLEDGED
        : AlarmStateFrench.RAISED;
    } else if (
      event.valueType === 'BOOLEAN' &&
      event.value !== 'false' &&
      event.value !== 'true'
    ) {
      if (AlarmStateFrench[event.value]) {
        return AlarmStateFrench[event.value];
      } else {
        return event.value;
      }
    } else {
      // no event type
      if (AlarmStateFrench[event.value]) {
        return AlarmStateFrench[event.value];
      } else {
        const alarmStateProvided = AlarmStateFrench.values().find(
          (e) => e === event.value.toLocaleUpperCase()
        );
        if (!!alarmStateProvided) {
          return alarmStateProvided;
        } else {
          // ex. : 'true' | 'false'
          return '';
        }
      }
    }
  }

  getEventAlarmState(eventValue: any): string {
    const viewValue = this.alarmStateTranslations.values().find(state => state[this.language] === eventValue)?.key;
    return viewValue ? this.translate.instant('alarms.states.' + viewValue) : eventValue;
  }
}
